import React, { useEffect, useRef, useCallback, useState } from 'react';
import './ScrollStack.css';
import ScrollProgressBar from './ScrollProgressBar';

export const ScrollStackItem = ({ children, itemClassName = '' }) => (
  <div className={`scroll-stack-card ${itemClassName}`.trim()}>{children}</div>
);

const ScrollStack = ({
  children,
  className = '',

  /** spacing control */
  itemDistance = 100,
  itemScale = 0.03,
  /** vertical gap between PARKED cards */
  itemStackDistance = 28,

  /**
   * Where the FIRST card parks inside the space below the header
   * (percentage of (viewportHeight - headerHeight - headerSafeGap)).
   */
  stackPosition = '22%',

  baseScale = 0.88,
  rotationAmount = 0,
  blurAmount = 0,

  /** Pinned header inside the viewport */
  header = null,
  headerHeight = 160,
  /** keep a safety gap under the title/subtitle */
  headerSafeGap = 24,

  /** optional external scroll container */
  externalScrollSelector = null,
  onStackComplete,
  firstCardVisible = true,
}) => {
  const rootRef = useRef(null);
  const viewportRef = useRef(null);
  const cardsRef = useRef([]);
  const scrollElRef = useRef(null);
  const stackedOnceRef = useRef(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // smaller => less scroll needed per-card
  const STEP_VH_FACTOR = 0.10;
  const KICKOFF_OFFSET_PX = 0;

  /** how much older cards peek upward above the top card (per depth) */
  const PEEK_OFFSET = 14; // px

  const toPx = useCallback((val, basePx) => {
    if (typeof val === 'string' && val.includes('%')) {
      return (parseFloat(val) / 100) * basePx;
    }
    return Number(val || 0);
  }, []);
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const easeOutCubic = (t) => (t <= 0 ? 0 : t >= 1 ? 1 : 1 - Math.pow(1 - t, 3));

  const update = useCallback(() => {
    const root = rootRef.current;
    const viewport = viewportRef.current;
    const cards = cardsRef.current;
    const scrollEl = scrollElRef.current;
    if (!root || !viewport || !cards.length || !scrollEl) return;

    const vh = viewport.clientHeight;
    const sectionTop = root.offsetTop;
    const scrollTop = (scrollEl === document.documentElement ? window.scrollY : scrollEl.scrollTop) || 0;
    const local = scrollTop - sectionTop;

    const n = cards.length;
    const step = vh * STEP_VH_FACTOR + itemDistance;
    const t = clamp((local - KICKOFF_OFFSET_PX) / step, 0, n + 0.999);
    
    // Calculate scroll progress (0 to 1)
    const totalScrollDistance = step * n;
    const currentProgress = Math.min(t / n, 1);
    setScrollProgress(currentProgress);

    // layout line under the header
    const remaining = Math.max(1, vh - headerHeight - headerSafeGap);
    const baseY = headerHeight + headerSafeGap + toPx(stackPosition, remaining);
    const startFromBottomY = vh + 1;

    // which two cards are transitioning
    const progressBetween = clamp(t, 0, n - 1);
    const topIndex = Math.floor(progressBetween);
    const phase = progressBetween - topIndex; // 0..1

    if (t >= n - 0.05 && !stackedOnceRef.current) {
      stackedOnceRef.current = true;
      onStackComplete?.();
    } else if (t < n - 0.05 && stackedOnceRef.current) {
      stackedOnceRef.current = false;
    }

    for (let i = 0; i < n; i++) {
      const card = cards[i];

      // card-specific reveal
      const boost = firstCardVisible && i === 0 ? 1 : 0;
      const d = t - i + boost;
      const reveal = easeOutCubic(clamp(d, 0, 1));

      // stack **downward** under the header for stability
      let targetY = baseY + i * itemStackDistance;

      // ---- NEW "peek" effect (like your screenshot) ----
      // Every older card (<= current top) lifts upward by a small amount per depth
      const depthAbove = Math.max(0, topIndex - i); // 0 for face card, 1 for previous, etc.
      if (depthAbove > 0) {
        // previous top begins to lift as phase grows; deeper cards are fully lifted
        const liftFactor = i === topIndex ? phase : 1;
        targetY -= PEEK_OFFSET * depthAbove * liftFactor;
      }

      // interpolate from offscreen bottom to target
      const y = startFromBottomY + (targetY - startFromBottomY) * reveal;

      // scaling — incoming emphasized, older cards smaller
      const parkedBase = Math.max(0.70, baseScale - i * itemScale);
      let parkedTarget = parkedBase;

      if (i === topIndex + 1) {
        // next incoming card slightly larger while taking the face position
        parkedTarget = Math.min(0.98, parkedBase + 0.10 * phase);
      } else if (i === topIndex) {
        // current face card shrinks a bit as the next arrives
        parkedTarget = Math.max(0.72, parkedBase - 0.08 * phase);
      } else if (i < topIndex) {
        // older layers a touch smaller per depth
        parkedTarget = Math.max(0.68, parkedBase - 0.06 * depthAbove);
      }

      const scale = (1 - reveal) * 1.0 + reveal * parkedTarget;
      const rot = rotationAmount ? (i * rotationAmount) * reveal : 0;

      let blur = 0;
      if (blurAmount) {
        blur = Math.max(0, (topIndex - i)) * blurAmount;
      }

      card.style.transform = `translate3d(-50%, ${y}px, 0) scale(${scale.toFixed(3)}) rotate(${rot.toFixed(2)}deg)`;
      card.style.filter = blur ? `blur(${blur.toFixed(1)}px)` : 'none';
      card.style.opacity = d < 0 ? 0 : 1;

      // latest cards above older ones
      card.style.zIndex = String(2500 + i);
      card.style.pointerEvents = 'auto';
    }
  }, [
    itemDistance,
    itemScale,
    itemStackDistance,
    stackPosition,
    baseScale,
    rotationAmount,
    blurAmount,
    headerHeight,
    headerSafeGap,
    toPx,
    onStackComplete,
    firstCardVisible
  ]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const viewport = viewportRef.current;
    const scrollEl = externalScrollSelector
      ? document.querySelector(externalScrollSelector)
      : document.documentElement;

    scrollElRef.current = scrollEl || document.documentElement;
    cardsRef.current = Array.from(root.querySelectorAll('.scroll-stack-card'));

    const layout = () => {
      const n = cardsRef.current.length;
      const vh = viewport ? viewport.clientHeight : window.innerHeight;
      const step = vh * STEP_VH_FACTOR + itemDistance;
      const total = Math.max(step * n + vh * 0.25, vh);
      const spacer = root.querySelector('.scroll-stack-spacer');
      spacer.style.height = `${total}px`;

      cardsRef.current.forEach((card) => {
        card.style.position = 'absolute';
        card.style.left = '50%';
        card.style.top = '0';
        card.style.width = 'min(100%, 980px)';
        card.style.boxSizing = 'border-box';
        card.style.transformOrigin = 'top center';
        card.style.willChange = 'transform, filter, opacity';
        card.style.borderRadius = '24px';
      });

      update();
    };

    layout();

    const onScroll = () => update();
    const onResize = () => layout();

    const target = scrollEl === document.documentElement ? window : scrollEl;
    target.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    return () => {
      target.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [externalScrollSelector, itemDistance, update]);

  return (
    <div className={`scroll-stack-root ${className}`.trim()} ref={rootRef}>
      <div className="scroll-stack-viewport" ref={viewportRef}>
        {header && (
          <div
            className="scroll-stack-fixed-header"
            style={{ height: `${headerHeight}px` }}
            aria-hidden={false}
          >
            {header}
            <ScrollProgressBar progress={scrollProgress} />
          </div>
        )}
        <div className="scroll-stack-layer" aria-hidden={false}>
          {React.Children.map(children, (child) => child)}
        </div>
      </div>
      <div className="scroll-stack-spacer" />
    </div>
  );
};

export default ScrollStack;
